import React, { useState } from 'react';
import { sans, mono, green, greenLight, greenDark, red, redLight, redDark, amber, amberLight, amberDark, useTheme } from '../tokens';
import { getDeadlineStatus, formatDate, formatTimestamp } from '../helpers';

const purple = '#7C3AED';
const purpleLight = '#EDE9FE';
const purpleDark = '#5B21B6';
// Preparer uses a teal-green, reviewer uses the brand green — tone on tone
const teal = '#0D9488';
const tealLight = '#CCFBF1';

// Financial statement sections
export const FS_SECTIONS = [
  { id: 'cash', label: 'Cash & Cash Equivalents', icon: '🏦', accounts: '11010–11110', oldSections: ['Cash - Bank Reconciliation', 'Cash - Float Reconciliation', 'Cash - FX Calculation'] },
  { id: 'investments', label: 'Investments', icon: '📈', accounts: '11200–11240', oldSections: ['Investments'] },
  { id: 'receivables', label: 'Accounts Receivable', icon: '📋', accounts: '12100–12800', oldSections: ['AR & Other Receivables'] },
  { id: 'sales-tax', label: 'Sales Tax', icon: '🧾', accounts: '12610–12611', oldSections: ['Sales Tax'] },
  { id: 'prepaids', label: 'Prepaids', icon: '📅', accounts: '14100', oldSections: ['Prepaids'] },
  { id: 'deposits', label: 'Deposits', icon: '🔑', accounts: '14200–14300', oldSections: ['Deposits'] },
  { id: 'fixed-assets', label: 'Fixed Assets', icon: '🏗️', accounts: '15010–15055', oldSections: ['Fixed Assets'] },
  { id: 'payables', label: 'Accounts Payable', icon: '💳', accounts: '20100–21100', oldSections: ['Credit Card Payables', 'Accounts Payable', 'Other Payables'] },
  { id: 'debt', label: 'Debt', icon: '🏛️', accounts: '23010–23015', oldSections: ['Bank Loan'] },
  { id: 'payroll', label: 'Payroll & Benefits', icon: '👥', accounts: '61010–64010', oldSections: ['P&L - Payroll'] },
];

function DeadlineBadge({ status }) {
  const styles = {
    'on-time': { bg: greenLight, color: greenDark, label: 'On time' },
    'due-soon': { bg: amberLight, color: amberDark, label: 'Due soon' },
    'overdue': { bg: redLight, color: redDark, label: 'Overdue' },
    'done': { bg: greenLight, color: greenDark, label: 'Done' },
    'blocked': { bg: purpleLight, color: purpleDark, label: 'Blocked' },
  };
  const s = styles[status] || styles['on-time'];
  return (
    <span style={{
      fontSize: 10, padding: '2px 8px', borderRadius: 12, fontWeight: 500,
      background: s.bg, color: s.color, fontFamily: sans, letterSpacing: 0.2,
      display: 'inline-block', textAlign: 'center', minWidth: 52,
    }}>
      {s.label}
    </span>
  );
}

// Clean square indicator — fills solid when active
function StatusSquare({ active, onClick, activeColor, label, disabled }) {
  const t = useTheme();
  return (
    <button
      onClick={(e) => { e.stopPropagation(); if (!disabled) onClick(); }}
      title={label}
      style={{
        width: 14, height: 14, borderRadius: 3,
        border: `1.5px solid ${active ? activeColor : disabled ? t.borderLight : t.borderMedium}`,
        background: active ? activeColor : 'transparent',
        cursor: disabled ? 'default' : 'pointer',
        flexShrink: 0, padding: 0,
        transition: 'all 0.2s ease',
        opacity: disabled ? 0.35 : 1,
      }}
    />
  );
}

// Editable date inline
function EditableDate({ value, onChange, color }) {
  const t = useTheme();
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <input
        type="date"
        defaultValue={value}
        autoFocus
        onChange={(e) => {
          if (e.target.value && e.target.value !== value) {
            onChange(e.target.value);
            setEditing(false);
          }
        }}
        onBlur={() => { setTimeout(() => setEditing(false), 200); }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setEditing(false);
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          fontFamily: mono, fontSize: 11, color: t.textPrimary,
          background: t.inputBg, border: `1px solid ${green}`,
          borderRadius: 4, padding: '2px 4px', width: 120,
          outline: 'none', cursor: 'text',
        }}
      />
    );
  }

  return (
    <span
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); setEditing(true); }}
      onMouseDown={(e) => e.stopPropagation()}
      title="Click to change deadline"
      style={{
        fontSize: 11, color, fontFamily: mono, whiteSpace: 'nowrap',
        textAlign: 'right', cursor: 'pointer',
        borderBottom: `1px dashed ${color}44`,
        padding: '2px 0',
      }}
    >
      {formatDate(value)}
    </span>
  );
}

const COL = {
  id: 24,
  name: 'minmax(0, 1fr)',
  preparer: 14,
  preparerLabel: 32,
  reviewer: 14,
  reviewerLabel: 32,
  due: 56,
  status: 64,
  timestamp: 110,
};

const gridTemplate = `${COL.id}px ${COL.name} ${COL.preparer}px ${COL.preparerLabel}px ${COL.reviewer}px ${COL.reviewerLabel}px ${COL.due}px ${COL.status}px ${COL.timestamp}px`;

function HeaderRow() {
  const t = useTheme();
  const hdr = { fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontWeight: 600, fontFamily: sans };
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: gridTemplate,
      gap: 6, padding: '6px 10px', marginBottom: 2,
      borderBottom: `1px solid ${t.border}`,
      alignItems: 'center',
    }}>
      <span style={{ ...hdr, textAlign: 'center' }}>#</span>
      <span style={hdr}>Task</span>
      <span style={{ ...hdr, textAlign: 'center', gridColumn: 'span 2' }}>Prep.</span>
      <span style={{ ...hdr, textAlign: 'center', gridColumn: 'span 2' }}>Rev.</span>
      <span style={{ ...hdr, textAlign: 'right' }}>Due</span>
      <span style={{ ...hdr, textAlign: 'center' }}>Status</span>
      <span style={{ ...hdr, textAlign: 'right' }}>Last action</span>
    </div>
  );
}

export default function CloseChecklist({ tasks, onToggleComplete, onToggleReview, onUpdateDeadline, onOpenTask, mockToday }) {
  const t = useTheme();
  const [filter, setFilter] = useState('all');
  const sections = [...new Set(tasks.map(t => t.section))];

  const filtered = tasks.filter(task => {
    if (filter === 'all') return true;
    if (filter === 'blocked') return task.blocked;
    const status = task.blocked ? 'blocked' : getDeadlineStatus(task.dueDate, task.done, mockToday);
    if (filter === 'overdue') return status === 'overdue';
    if (filter === 'due-soon') return status === 'due-soon' || status === 'overdue';
    if (filter === 'pending-review') return task.done && !task.reviewed;
    return true;
  });

  const overdueCount = tasks.filter(task => !task.blocked && getDeadlineStatus(task.dueDate, task.done, mockToday) === 'overdue').length;
  const dueSoonCount = tasks.filter(task => !task.blocked && getDeadlineStatus(task.dueDate, task.done, mockToday) === 'due-soon').length;
  const pendingReviewCount = tasks.filter(task => task.done && !task.reviewed).length;
  const blockedCount = tasks.filter(task => task.blocked).length;

  const filters = [
    { key: 'all', label: 'All', count: tasks.length },
    { key: 'overdue', label: 'Overdue', count: overdueCount },
    { key: 'due-soon', label: 'Due soon', count: dueSoonCount },
    { key: 'pending-review', label: 'Needs review', count: pendingReviewCount },
    ...(blockedCount > 0 ? [{ key: 'blocked', label: 'Blocked', count: blockedCount }] : []),
  ];

  const getLatestTimestamp = (task) => {
    if (task.reviewedAt) return formatTimestamp(task.reviewedAt);
    if (task.completedAt) return formatTimestamp(task.completedAt);
    return null;
  };

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
        {filters.map(f => {
          const active = filter === f.key;
          const accent = f.key === 'blocked' ? purple : f.key === 'overdue' ? red : green;
          const accentBg = f.key === 'blocked' ? purpleLight : f.key === 'overdue' ? redLight : greenLight;
          const accentDark = f.key === 'blocked' ? purpleDark : f.key === 'overdue' ? redDark : greenDark;
          return (
            <button key={f.key} onClick={() => setFilter(f.key)} style={{
              padding: '4px 12px', borderRadius: 14,
              border: `1px solid ${active ? accent : t.border}`,
              background: active ? accentBg : 'transparent',
              color: active ? accentDark : t.textSecondary,
              fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: sans,
            }}>
              {f.label}
              {f.count > 0 && <span style={{ marginLeft: 4, fontFamily: mono, fontSize: 10 }}>({f.count})</span>}
            </button>
          );
        })}
      </div>

      <HeaderRow />

      {sections.map(section => {
        const sectionTasks = filtered.filter(task => task.section === section);
        if (sectionTasks.length === 0) return null;
        const sectionDone = sectionTasks.filter(task => task.done).length;
        const sectionReviewed = sectionTasks.filter(task => task.reviewed).length;
        const sectionTotal = sectionTasks.length;
        return (
          <div key={section} style={{ marginBottom: 10 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              margin: '10px 0 3px', padding: '0 10px',
            }}>
              <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, color: t.textMuted, fontWeight: 600, fontFamily: sans }}>{section}</span>
              <span style={{ fontSize: 10, color: sectionReviewed === sectionTotal ? green : t.textFaint, fontFamily: mono }}>
                {sectionDone}/{sectionTotal}
              </span>
            </div>
            {sectionTasks.map(task => {
              const isBlocked = task.blocked;
              const dlStatus = isBlocked ? 'blocked' : getDeadlineStatus(task.dueDate, task.done, mockToday);
              const dueDateColor = dlStatus === 'overdue' ? red : dlStatus === 'due-soon' ? amber : dlStatus === 'blocked' ? purple : t.textFaint;
              const rowBorder = dlStatus === 'overdue' ? redLight : isBlocked ? purpleLight : t.border;

              let rowBg = t.bgCard;
              if (task.reviewed) rowBg = greenLight + '40';
              else if (task.done) rowBg = tealLight + '50';
              else if (isBlocked) rowBg = t.bg === '#FFFFFF' ? '#FDFAFF' : t.bgCard;

              const latestTs = getLatestTimestamp(task);

              return (
                <div key={task.id} onClick={() => onOpenTask(task.id)} style={{
                  display: 'grid', gridTemplateColumns: gridTemplate,
                  gap: 6, alignItems: 'center', padding: '6px 10px',
                  border: `1px solid ${rowBorder}`, borderRadius: 5, marginBottom: 2, cursor: 'pointer',
                  background: rowBg,
                  transition: 'all 0.2s ease',
                  opacity: isBlocked ? 0.6 : 1,
                }}>
                  {/* Row number */}
                  <span style={{ fontSize: 9, color: t.textFaint, fontFamily: mono, textAlign: 'center' }}>
                    {task.id}
                  </span>

                  {/* Task name */}
                  <div style={{ minWidth: 0, overflow: 'hidden' }}>
                    <div style={{
                      fontSize: 12, fontFamily: sans, fontWeight: 400,
                      textDecoration: task.reviewed ? 'line-through' : 'none',
                      color: task.reviewed ? t.textMuted : task.done ? t.textSecondary : t.textPrimary,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{task.name}</div>
                    {task.accounts && (
                      <div style={{ fontSize: 9, color: t.textFaint, fontFamily: mono, marginTop: 1 }}>{task.accounts}</div>
                    )}
                  </div>

                  {/* Preparer square */}
                  <StatusSquare
                    active={task.done}
                    onClick={() => !isBlocked && onToggleComplete(task.id)}
                    activeColor={teal}
                    label={task.done ? `Prepared by ${task.completedBy}` : `Mark as prepared (${task.assignedTo})`}
                    disabled={isBlocked}
                  />

                  {/* Preparer initials — only show when done */}
                  <span style={{
                    fontSize: 10, fontFamily: mono, fontWeight: 500,
                    color: teal,
                    textAlign: 'center',
                  }}>
                    {task.done ? task.completedBy : ''}
                  </span>

                  {/* Reviewer square */}
                  <StatusSquare
                    active={task.reviewed}
                    onClick={() => task.done && !isBlocked && onToggleReview(task.id)}
                    activeColor={green}
                    label={task.reviewed ? `Reviewed by ${task.reviewedBy}` : task.done ? `Mark as reviewed (${task.reviewer})` : 'Complete task first'}
                    disabled={!task.done || isBlocked}
                  />

                  {/* Reviewer initials — only show when reviewed */}
                  <span style={{
                    fontSize: 10, fontFamily: mono, fontWeight: 500,
                    color: green,
                    textAlign: 'center',
                  }}>
                    {task.reviewed ? task.reviewedBy : ''}
                  </span>

                  {/* Due date (editable) */}
                  <div style={{ textAlign: 'right' }}>
                    <EditableDate
                      value={task.dueDate}
                      onChange={(newDate) => onUpdateDeadline(task.id, newDate)}
                      color={dueDateColor}
                    />
                  </div>

                  {/* Status badge */}
                  <div style={{ textAlign: 'center' }}>
                    <DeadlineBadge status={dlStatus} />
                  </div>

                  {/* Timestamp */}
                  <div style={{ textAlign: 'right' }}>
                    {latestTs ? (
                      <span style={{ fontSize: 9, fontFamily: mono, color: t.textFaint, whiteSpace: 'nowrap' }}>
                        {latestTs}
                      </span>
                    ) : (
                      <span style={{ fontSize: 9, color: t.textFaint }}>{'\u2014'}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: t.textFaint, fontSize: 13 }}>
          No tasks match this filter
        </div>
      )}
    </div>
  );
}
